import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Button } from "@/components/ui/Button";
import PrintButton from "@/components/ui/PrintButton";
import ExcelButton from "@/components/ui/ExcelButton";
import Table from "@/components/ui/Table";
import { InputBox } from "@/components/ui/InputBox";
import { CheckBox } from "@/components/ui/CheckBox";
import { NumberInputBox } from "@/components/ui/NumberInputBox";
import SelectBox from "@/components/ui/SelectBox";
import CountBox from "@/components/ui/CountBox";
import { useMemo, useState, useEffect, useCallback } from "react";
import { TableColumn } from "@/components/ui/Table";
import { useMonthlyTransactions } from "@/hooks/settlement/se_monthlyTransactions/useMonthlyTransactions";
import { MonthlyTransactionsDto } from "@/interface/settlement/monthlyTransactions/index";
import { formatNumber } from "@/lib/utils";
import { useCodeListByCompanyCode } from "@/hooks/standard/st_code/useCode";
import { Code } from "@/interface/standard/st_code/index";

// 연월 목록 생성 유틸리티
const generateYearMonths = (
  startYear: string,
  startMonth: string,
  endYear: string,
  endMonth: string
): { year: number; month: number; key: string; label: string }[] => {
  const result: { year: number; month: number; key: string; label: string }[] = [];
  const sYear = Number(startYear);
  const sMonth = Number(startMonth);
  const eYear = Number(endYear);
  const eMonth = Number(endMonth);

  if (isNaN(sYear) || isNaN(sMonth) || isNaN(eYear) || isNaN(eMonth)) {
    return result;
  }

  let currentYear = sYear;
  let currentMonth = sMonth;

  while (currentYear < eYear || (currentYear === eYear && currentMonth <= eMonth)) {
    const key = `${currentYear}${String(currentMonth).padStart(2, "0")}`;
    const label = `${currentYear}년 ${currentMonth}월`;
    result.push({ year: currentYear, month: currentMonth, key, label });

    currentMonth++;
    if (currentMonth > 12) {
      currentMonth = 1;
      currentYear++;
    }
  }

  return result;
};

export default function MonthlyTransactionsPage() {
  const currentYear = new Date().getFullYear().toString();

  const [company, setCompany] = useState("");
  const [actualCompany, setActualCompany] = useState("");

  const [startYear, setStartYear] = useState(currentYear);
  const [actualStartYear, setActualStartYear] = useState(currentYear);

  const [endYear, setEndYear] = useState(currentYear);
  const [actualEndYear, setActualEndYear] = useState(currentYear);

  const [searchValue, setSearchValue] = useState("");
  const [actualSearchValue, setActualSearchValue] = useState("");

  const [searchType, setSearchType] = useState("stCompanyNameShort");
  const [actualSearchType, setActualSearchType] = useState("stCompanyNameShort");

  const [startMonthPicker, setStartMonthPicker] = useState("01");
  const [actualStartMonth, setActualStartMonth] = useState("01");

  const [endMonthPicker, setEndMonthPicker] = useState("12");
  const [actualEndMonth, setActualEndMonth] = useState("12");

  const [exactMatch, setExactMatch] = useState(false);
  const [actualExactMatch, setActualExactMatch] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [allData, setAllData] = useState<MonthlyTransactionsDto[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [tableVer, setTableVer] = useState(0);
  const { data: companyCodeList = [] } = useCodeListByCompanyCode();

  const { data: monthlyTransactionsData, isLoading } = useMonthlyTransactions({
    startYear: actualStartYear,
    startMonth: actualStartMonth,
    endYear: actualEndYear,
    endMonth: actualEndMonth,
    searchType: actualSearchType,
    search: actualSearchValue,
    exactMatch: actualExactMatch,
    company: actualCompany,
    page: currentPage,
    size: 20,
  });

  // 연도/월 변경 시 실시간 조회
  useEffect(() => {
    setCurrentPage(0);
    setTotalElements(0);
    setAllData([]);
    setLoadingMore(false);
    setActualStartYear(startYear);
    setActualEndYear(endYear);
    setActualStartMonth(startMonthPicker);
    setActualEndMonth(endMonthPicker);
    setTableVer(v => v + 1);
  }, [startYear, endYear, startMonthPicker, endMonthPicker]);

  useEffect(() => {
    setActualSearchType(searchType);
    setActualCompany(company);
  }, []);

  const monthOptions = [
    { label: "1월", value: "01" },
    { label: "2월", value: "02" },
    { label: "3월", value: "03" },
    { label: "4월", value: "04" },
    { label: "5월", value: "05" },
    { label: "6월", value: "06" },
    { label: "7월", value: "07" },
    { label: "8월", value: "08" },
    { label: "9월", value: "09" },
    { label: "10월", value: "10" },
    { label: "11월", value: "11" },
    { label: "12월", value: "12" },
  ];

  const companyOptions = useMemo(
    () =>
      companyCodeList.map((item: Code) => ({
        value: item.code || item.codeId?.toString() || "",
        label: item.name || "",
      })) ?? [],
    [companyCodeList]
  );

  const handleReset = () => {
    // 화면 조건 초기화
    setSearchValue("");
    setSearchType("stCompanyNameShort");
    setStartYear(currentYear);
    setEndYear(currentYear);
    setStartMonthPicker("01");
    setEndMonthPicker("12");
    setCompany("");
    setExactMatch(false);

    // 실제 검색 조건 초기화
    setActualSearchValue("");
    setActualSearchType("stCompanyNameShort");
    setActualStartYear(currentYear);
    setActualEndYear(currentYear);
    setActualStartMonth("01");
    setActualEndMonth("12");
    setActualCompany("");
    setActualExactMatch(false);

    setCurrentPage(0);
    setTotalElements(0);
    setAllData([]);
    setTableVer(v => v + 1);
  };

  useEffect(() => {
    setCurrentPage(0);
    setTotalElements(0);
    setLoadingMore(false);
    setTableVer(v => v + 1);
  }, [
    actualSearchValue,
    actualSearchType,
    actualStartMonth,
    actualEndMonth,
    actualStartYear,
    actualEndYear,
    actualCompany,
  ]);

  useEffect(() => {
    if (monthlyTransactionsData) {
      if (currentPage === 0) {
        setAllData(monthlyTransactionsData.content || []);
        setTotalElements(monthlyTransactionsData.totalElements || 0);
      } else {
        setAllData(prev => [...prev, ...(monthlyTransactionsData.content || [])]);
      }
    }
  }, [monthlyTransactionsData, currentPage]);

  useEffect(() => {
    if (monthlyTransactionsData) {
      setLoadingMore(false);
    }
  }, [monthlyTransactionsData]);

  const hasMore = useMemo(() => {
    if (!monthlyTransactionsData) return false;
    return !monthlyTransactionsData.last && allData.length < totalElements;
  }, [monthlyTransactionsData?.last, allData.length, totalElements]);

  const handleTableLoadMore = useCallback(
    async (currentCount: number) => {
      if (!hasMore || loadingMore) {
        return;
      }

      if (currentCount >= allData.length * 0.8) {
        setLoadingMore(true);
        setCurrentPage(prev => prev + 1);
      }
    },
    [hasMore, loadingMore, allData.length]
  );

  const handleSearch = () => {
    setCurrentPage(0);
    setTotalElements(0);
    setAllData([]);
    setLoadingMore(false);
    setActualSearchValue(searchValue);
    setActualSearchType(searchType);
    setActualExactMatch(exactMatch);
    setActualStartMonth(startMonthPicker);
    setActualEndMonth(endMonthPicker);
    setActualStartYear(startYear);
    setActualEndYear(endYear);
    setActualCompany(company);
    setTableVer(v => v + 1);
  };

  const staticColumns: TableColumn[] = useMemo(
    () => [
      { key: "saleSlipCustCode", header: "업체코드", bodyAlign: "center" },
      { key: "stCompanyNameShort", header: "업체명", bodyAlign: "left" },
      { key: "stCompanyNumber", header: "사업자등록번호", bodyAlign: "left" },
    ],
    []
  );

  // 연월 범위 기반 동적 컬럼 생성
  const monthRangeColumns: TableColumn[] = useMemo(() => {
    const yearMonths = generateYearMonths(actualStartYear, actualStartMonth, actualEndYear, actualEndMonth);
    const dynamicColumns: TableColumn[] = [];

    for (const ym of yearMonths) {
      const groupLabel = `${ym.year}년 ${ym.month}월`;

      dynamicColumns.push(
        {
          key: `monthlyCnt_${ym.key}`,
          header: "건수",
          bodyAlign: "right",
          group: groupLabel,
          render: (_v: number, row: MonthlyTransactionsDto) =>
            formatNumber(row.monthlyData?.[`monthlyCnt_${ym.key}`] ?? 0),
        },
        {
          key: `monthlySupport_${ym.key}`,
          header: "공급가",
          bodyAlign: "right",
          group: groupLabel,
          render: (_v: number, row: MonthlyTransactionsDto) =>
            formatNumber(row.monthlyData?.[`monthlySupport_${ym.key}`] ?? 0),
        },
        {
          key: `monthlySurtax_${ym.key}`,
          header: "부가세",
          bodyAlign: "right",
          group: groupLabel,
          render: (_v: number, row: MonthlyTransactionsDto) =>
            formatNumber(row.monthlyData?.[`monthlySurtax_${ym.key}`] ?? 0),
        }
      );
    }

    return dynamicColumns;
  }, [actualStartYear, actualStartMonth, actualEndYear, actualEndMonth]);

  // 총계 컬럼
  const summaryColumns: TableColumn[] = useMemo(() => {
    const yearMonths = generateYearMonths(actualStartYear, actualStartMonth, actualEndYear, actualEndMonth);

    return [
      {
        key: "totalCnt",
        header: "건수",
        bodyAlign: "right",
        group: "총계",
        render: (_v: number, row: MonthlyTransactionsDto) => {
          let total = 0;
          for (const ym of yearMonths) {
            total += row.monthlyData?.[`monthlyCnt_${ym.key}`] ?? 0;
          }
          return formatNumber(total);
        },
      },
      {
        key: "totalSupport",
        header: "공급가",
        bodyAlign: "right",
        group: "총계",
        render: (_v: number, row: MonthlyTransactionsDto) => {
          let total = 0;
          for (const ym of yearMonths) {
            total += row.monthlyData?.[`monthlySupport_${ym.key}`] ?? 0;
          }
          return formatNumber(total);
        },
      },
      {
        key: "totalSurtax",
        header: "부가세",
        bodyAlign: "right",
        group: "총계",
        render: (_v: number, row: MonthlyTransactionsDto) => {
          let total = 0;
          for (const ym of yearMonths) {
            total += row.monthlyData?.[`monthlySurtax_${ym.key}`] ?? 0;
          }
          return formatNumber(total);
        },
      },
    ];
  }, [actualStartYear, actualStartMonth, actualEndYear, actualEndMonth]);

  const columns: TableColumn[] = useMemo(
    () => [...staticColumns, ...monthRangeColumns, ...summaryColumns],
    [staticColumns, monthRangeColumns, summaryColumns]
  );

  return (
    <div className="flex flex-col h-full">
      {/* 검색영역 */}
      <SearchBoxLayout>
        <div className="flex items-end">
          <SelectBox
            label="회사"
            className="w-50"
            value={company}
            onChange={setCompany}
            options={[{ label: "선택", value: "" }, ...companyOptions]}
          />
          <div className="mr-3" />
          <SelectBox
            label="구분"
            className="w-35"
            value={searchType}
            onChange={setSearchType}
            options={[
              { label: "업체명", value: "stCompanyNameShort" },
              { label: "사업자등록번호", value: "stCompanyNumber" },
            ]}
          />
          <div className="mr-1" />
          <InputBox
            label=""
            className="w-50"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
          <div className="mr-3" />
          <div className="flex items-end gap-2">
            <NumberInputBox
              label="시작"
              value={startYear}
              onChange={e => setStartYear(e.target.value as string)}
              min={2000}
              max={2100}
              step={1}
              className="w-24"
            />
            <SelectBox
              label=""
              className="w-22"
              value={startMonthPicker}
              onChange={setStartMonthPicker}
              options={monthOptions}
            />
            <span className="pb-2 text-lg font-medium">~</span>
            <NumberInputBox
              label="종료"
              value={endYear}
              onChange={e => setEndYear(e.target.value as string)}
              min={2000}
              max={2100}
              step={1}
              className="w-24"
            />
            <SelectBox
              label=""
              className="w-22"
              value={endMonthPicker}
              onChange={setEndMonthPicker}
              options={monthOptions}
            />
          </div>
          <div className="mr-2" />
          <div className="flex gap-[6px] ml-auto items-end">
            <CheckBox
              label="일치"
              labelPosition="right"
              checked={exactMatch}
              onChange={e => setExactMatch(e.target.checked)}
            />
            <Button variant="reset" onClick={handleReset}>
              초기화
            </Button>
            <Button variant="search" onClick={handleSearch}>
              조회
            </Button>
          </div>
        </div>
      </SearchBoxLayout>

      {/* 상단 버튼/카운트 */}
      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2">
          <CountBox count={allData.length} />
          <div className="flex gap-2">
            <PrintButton data={allData} columns={columns} title="월별거래내역" skipPermissionCheck />
            <ExcelButton data={allData} columns={columns} fileName="월별거래내역" skipPermissionCheck />
          </div>
        </div>

        {/* 본문 표 */}
        <div className="flex-1 min-h-0">
          <Table
            key={`monthly-transactions-table-v${tableVer}`}
            data={allData}
            className="h-full"
            showRowNumber
            resizable
            columns={columns}
            infiniteScroll={true}
            initialLoadCount={20}
            loadMoreCount={20}
            maxHeight="100%"
            hasMore={hasMore}
            onLoadMore={handleTableLoadMore}
            loadingMore={loadingMore}
            loading={isLoading && allData.length === 0}
          />
          {isLoading && allData.length > 0 && (
            <div className="text-center py-2 text-gray-500">새 페이지 로딩 중...</div>
          )}
        </div>
      </main>
    </div>
  );
}
